import {
  MenuItemAddPayload,
  MenuItemChangeTypePayload,
  MenuItemChangeVisibilityPayload,
  MenuItemMovePayload,
  MenuItemUpdatePayload,
  MenuItemUpdateSlugPayload,
} from './menu';
import {
  BlockActionsData,
  BlockDataData,
  BlockIndexData,
  BlockPlaceData,
  BlockSizeData,
  BlockUpdateData,
  BlockVisibilityData,
  ChatBlockNoAccess,
  PortalBlock,
  TaskBlockNoAccess,
} from './portal-block';
import { PortalPage } from './portal-page';
import { PortalSettings } from './portal-settings';
import { PortalThemeConfigPartial } from './portal-theme';
import { VirtualPortalNode } from './virtual-portal-node';

export enum ChangeEventType {
  ADD_BLOCK = 'addBlock',
  UPDATE_BLOCK = 'updateBlock',
  UPDATE_BLOCK_ACTIONS = 'updateBlockActions',
  DELETE_BLOCK = 'deleteBlock',
  UPDATE_BLOCK_INDEX = 'updateBlockIndex',
  UPDATE_BLOCK_PLACE = 'updateBlockPlace',
  UPDATE_BLOCK_SIZE = 'updateBlockSize',
  UPDATE_BLOCK_VISIBILITY = 'updateBlockVisibility',
  PUSH_BLOCK_DATA = 'pushBlockData',
  ADD_MENU_ITEM = 'addMenuItem',
  MOVE_MENU_ITEM = 'moveMenuItem',
  DELETE_MENU_ITEM = 'deleteMenuItem',
  UPDATE_MENU_ITEM = 'updateMenuItem',
  UPDATE_MENU_ITEM_SLUG = 'updateMenuItemSlug',
  CHANGE_MENU_ITEM_VISIBILITY = 'changeMenuItemVisibility',
  CHANGE_MENU_ITEM_TYPE = 'changeMenuItemType',
  ADD_NODE = 'addNode',
  DELETE_NODE = 'deleteNode',
  UPDATE_THEME_SETTINGS = 'updateThemeSettings',
  UPDATE_SEARCH_ANALYTICS_SETTINGS = 'updateSearchAnalyticsSettings',
  UPDATE_PORTAL_ANALYTICS_SETTINGS = 'updatePortalAnalyticsSettings',
  UPDATE_SEARCH_INDEX_SETTINGS = 'updateSearchIndexSettings',
  UPDATE_ACCESS_SETTINGS = 'updateAccessSettings',
  UPDATE_CUSTOM_CODE_SETTINGS = 'updateCustomCodeSettings',
  UPDATE_META_SETTINGS = 'updateMetaSettings',
  UPDATE_BRANDING_SETTING = 'updateBrandingSetting',
  UPDATE_HERO_SETTING = 'updateHeroSetting',
  UPDATE_PAGE = 'updatePage',
  CHANGE_PAGE_VISIBILITY = 'changePageVisibility',
  UPDATE_REQUEST_ACCESS_PAGE = 'updateRequestAccessPage',
  UPDATE_CLIENT_SETTINGS = 'updateClientSettings',
  UPDATE_AI_ASSISTANT_SETTINGS = 'updateAIAssistantSettings',
}

export interface ChangeEventMeta {
  userId: number;
  pageTitle: string;
  updatedAt: number;
  pageId: string;
  path: string;
}

export interface ChangeEventBase<NewValue = undefined, OldValue = NewValue> {
  type: ChangeEventType;
  newValue: NewValue;
  oldValue: OldValue;
  batchId?: string;
  squashId?: string;
  collectId?: string;
}
export type ChangeUndoCollection = Required<Pick<ChangeEventBase, 'collectId'>>;

export interface AddBlockEvent
  extends ChangeEventBase<Exclude<PortalBlock, ChatBlockNoAccess | TaskBlockNoAccess>, undefined> {
  type: ChangeEventType.ADD_BLOCK;
}

export interface UpdateBlockEvent extends ChangeEventBase<BlockUpdateData> {
  type: ChangeEventType.UPDATE_BLOCK;
}
export interface UpdateBlockActionsEvent extends ChangeEventBase<BlockActionsData> {
  type: ChangeEventType.UPDATE_BLOCK_ACTIONS;
}
export interface DeleteBlockEvent
  extends ChangeEventBase<undefined, Exclude<PortalBlock, ChatBlockNoAccess | TaskBlockNoAccess>> {
  type: ChangeEventType.DELETE_BLOCK;
}

export interface UpdateBlockIndexEvent extends ChangeEventBase<BlockIndexData> {
  type: ChangeEventType.UPDATE_BLOCK_INDEX;
}

export interface UpdateBlockPlaceEvent extends ChangeEventBase<BlockPlaceData> {
  type: ChangeEventType.UPDATE_BLOCK_PLACE;
}

export interface UpdateBlockSizeEvent extends ChangeEventBase<BlockSizeData> {
  type: ChangeEventType.UPDATE_BLOCK_SIZE;
}

export interface UpdateBlockVisibilityEvent extends ChangeEventBase<BlockVisibilityData> {
  type: ChangeEventType.UPDATE_BLOCK_VISIBILITY;
}

export interface PushBlockDataEvent extends ChangeEventBase<BlockDataData, undefined> {
  type: ChangeEventType.PUSH_BLOCK_DATA;
}

export interface AddMenuItemEvent extends ChangeEventBase<MenuItemAddPayload, undefined> {
  type: ChangeEventType.ADD_MENU_ITEM;
}

export interface DeleteMenuItemEvent extends ChangeEventBase<undefined, MenuItemAddPayload> {
  type: ChangeEventType.DELETE_MENU_ITEM;
}

export interface UpdateMenuItemEvent extends ChangeEventBase<MenuItemUpdatePayload> {
  type: ChangeEventType.UPDATE_MENU_ITEM;
}

export interface ChangeMenuItemVisibilityEvent extends ChangeEventBase<MenuItemChangeVisibilityPayload> {
  type: ChangeEventType.CHANGE_MENU_ITEM_VISIBILITY;
}

export interface ChangeMenuItemTypeEvent extends ChangeEventBase<MenuItemChangeTypePayload> {
  type: ChangeEventType.CHANGE_MENU_ITEM_TYPE;
}

export interface UpdateMenuItemSlugEvent extends ChangeEventBase<MenuItemUpdateSlugPayload> {
  type: ChangeEventType.UPDATE_MENU_ITEM_SLUG;
}
export interface MoveMenuItemEvent extends ChangeEventBase<MenuItemMovePayload> {
  type: ChangeEventType.MOVE_MENU_ITEM;
}

export interface AddVirtualNodeEvent extends ChangeEventBase<VirtualPortalNode, undefined> {
  type: ChangeEventType.ADD_NODE;
}

export interface DeleteVirtualNodeEvent extends ChangeEventBase<undefined, PortalPage> {
  type: ChangeEventType.DELETE_NODE;
}

export interface UpdatePageEvent extends ChangeEventBase<{ id: string; title?: string; path: string }> {
  type: ChangeEventType.UPDATE_PAGE;
}
export interface ChangePageVisibilityEvent extends ChangeEventBase<{ id: string; visible: boolean }> {
  type: ChangeEventType.CHANGE_PAGE_VISIBILITY;
}

export interface UpdateThemeEvent extends ChangeEventBase<PortalThemeConfigPartial> {
  type: ChangeEventType.UPDATE_THEME_SETTINGS;
}

export interface UpdateSearchAnalyticsEvent extends ChangeEventBase<Pick<PortalSettings, 'searchAnalyticsEnabled'>> {
  type: ChangeEventType.UPDATE_SEARCH_ANALYTICS_SETTINGS;
}
export interface UpdatePortalAnalyticsEvent extends ChangeEventBase<Pick<PortalSettings, 'portalAnalyticsEnabled'>> {
  type: ChangeEventType.UPDATE_PORTAL_ANALYTICS_SETTINGS;
}
export interface UpdateSearchIndexEvent extends ChangeEventBase<Pick<PortalSettings, 'searchIndexEnabled'>> {
  type: ChangeEventType.UPDATE_SEARCH_INDEX_SETTINGS;
}
export interface UpdateAccessEvent extends ChangeEventBase<Pick<PortalSettings, 'access'>> {
  type: ChangeEventType.UPDATE_ACCESS_SETTINGS;
}
export interface UpdateCustomCodeEvent
  extends ChangeEventBase<Pick<PortalSettings, 'customCode' | 'customStyle' | 'customScript'>> {
  type: ChangeEventType.UPDATE_CUSTOM_CODE_SETTINGS;
}

export interface UpdateMetaSettingsEvent extends ChangeEventBase<Pick<PortalSettings, 'name' | 'description'>> {
  type: ChangeEventType.UPDATE_META_SETTINGS;
}

export interface UpdateBrandingSettingEvent extends ChangeEventBase<Pick<PortalSettings, 'disableBranding'>> {
  type: ChangeEventType.UPDATE_BRANDING_SETTING;
}

export interface UpdateHeroSettingEvent
  extends ChangeEventBase<
    Pick<PortalSettings, 'searchEnabled' | 'greetingEnabled' | 'greetingMessage' | 'headerBlocksEnabled'>
  > {
  type: ChangeEventType.UPDATE_HERO_SETTING;
}

export interface UpdateRequestAccessPageEvent extends ChangeEventBase<Pick<PortalSettings, 'requestAccessPage'>> {
  type: ChangeEventType.UPDATE_REQUEST_ACCESS_PAGE;
}

export interface UpdateClientSettingsEvent extends ChangeEventBase<Pick<PortalSettings, 'clientSettings'>> {
  type: ChangeEventType.UPDATE_CLIENT_SETTINGS;
}

export interface UpdateAIAssistantSettingsEvent extends ChangeEventBase<Pick<PortalSettings, 'aiAssistantSettings'>> {
  type: ChangeEventType.UPDATE_AI_ASSISTANT_SETTINGS;
}

export type ChangeEventPayload =
  | AddBlockEvent
  | UpdateBlockEvent
  | DeleteBlockEvent
  | UpdateBlockIndexEvent
  | UpdateBlockPlaceEvent
  | UpdateBlockActionsEvent
  | UpdateBlockSizeEvent
  | UpdateBlockVisibilityEvent
  | PushBlockDataEvent
  | AddMenuItemEvent
  | DeleteMenuItemEvent
  | UpdateMenuItemEvent
  | ChangeMenuItemVisibilityEvent
  | ChangeMenuItemTypeEvent
  | UpdateMenuItemSlugEvent
  | AddVirtualNodeEvent
  | DeleteVirtualNodeEvent
  | UpdatePageEvent
  | ChangePageVisibilityEvent
  | MoveMenuItemEvent
  | UpdateThemeEvent
  | UpdateSearchAnalyticsEvent
  | UpdatePortalAnalyticsEvent
  | UpdateSearchIndexEvent
  | UpdateAccessEvent
  | UpdateCustomCodeEvent
  | UpdateMetaSettingsEvent
  | UpdateBrandingSettingEvent
  | UpdateHeroSettingEvent
  | UpdateRequestAccessPageEvent
  | UpdateClientSettingsEvent
  | UpdateAIAssistantSettingsEvent;

export type ChangeEventWithPartialMeta = ChangeEventPayload & {
  meta?: Omit<Partial<ChangeEventMeta>, 'userId' | 'updatedAt'>;
};
export type ChangeEvent = ChangeEventPayload & { meta: ChangeEventMeta };

export const PORTAL_CHANGES_HISTORY_STORAGE_KEY = 'portal-changes';
export const PORTAL_CHANGES_CURSOR_STORAGE_KEY = 'portal-changes-cursor';
export const SIDEBAR_CHANGE_TYPES = [
  ChangeEventType.ADD_MENU_ITEM,
  ChangeEventType.DELETE_MENU_ITEM,
  ChangeEventType.UPDATE_MENU_ITEM,
  ChangeEventType.MOVE_MENU_ITEM,
  ChangeEventType.UPDATE_MENU_ITEM_SLUG,
  ChangeEventType.CHANGE_MENU_ITEM_TYPE,
  ChangeEventType.CHANGE_MENU_ITEM_VISIBILITY,
  ChangeEventType.ADD_NODE,
  ChangeEventType.DELETE_NODE,
];
